// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import { Bundle } from '@/bundle/api/types';
import {
  ApplyBundleDialog,
  BundleApplyLine,
} from '@/bundle/components/apply-bundle-dialog';
import { buildBundleAppliedToast } from '@/bundle/utilities/bundle-applied-toast';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  AddRounded,
  LibraryAddOutlined,
} from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useLinkedFieldFlourish } from '@/hooks/use-linked-field-flourish';
import {
  CustomPriorityGroup,
  isZoneOption,
} from '@/location/components/location-search-options';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import {
  Project,
  ProjectLineItem,
  TRANSACTION_TYPE,
} from '@/project/api/types';
import { UpdateProjectLineItem } from '@/project/api/update-project';
import { ProjectLineItemFormRow } from '@/project/components/project-line-item-form-row';
import { ProjectLineItemGantt } from '@/project/components/project-line-item-gantt';
import { GanttItem } from '@/project/components/project-line-item-gantt-types';
import { useUpdateProject } from '@/project/hooks/use-update-project';
import {
  ProjectLineItemsFormData,
  projectLineItemsFormSchema,
} from '@/project/types/project-line-items-form-schema';
import { computeContinuationDates } from '@/project/utilities/compute-continuation-dates';
import { getGroupColor } from '@/project/utilities/group-line-items';
import { validateLineItemDates } from '@/project/utilities/validate-line-item-dates';
import { SparseLocation } from '@/sparse/api/types';
import { formatCalendarDateRangeDuration } from '@/utilities/format-calendar-date-range-duration';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  BoxProps,
  Button,
  Collapse,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';

type FormData = ProjectLineItemsFormData;

function noop() {
  // Intentional no-op for ref initialization
}

function isSameDate(
  date: DateTime | null | undefined,
  otherDate: DateTime | null | undefined
) {
  return date?.toISODate() === otherDate?.toISODate();
}

export interface FormActions {
  submit: () => void;
  cancel: () => void;
}

interface Props extends Pick<BoxProps, 'padding'> {
  project: Pick<
    Project,
    | 'key'
    | 'items'
    | 'defaultLocation'
    | 'locations'
    | 'customer'
    | 'startOn'
    | 'endOn'
  >;
  onCancel: () => void;
  onSuccess: () => void;
  onFormReady?: (actions: FormActions) => void;
}

/**
 * Converts a ProjectLineItem to form data format.
 * Note: lineItem uses nested structure with id/key at top level (binding IDs)
 * and catalog item properties nested under `item`.
 */
function lineItemToFormData(
  lineItem: ProjectLineItem
): FormData['lineItems'][number] {
  return {
    id: lineItem.id,
    item: lineItem.item,
    quantity: lineItem.quantity,
    details: lineItem.details,
    location: lineItem.location,
    transactionType: lineItem.transactionType,
    startOn: lineItem.startOn,
    endOn: lineItem.endOn,
    aggregationKey: lineItem.aggregationKey,
    hasInvoicedCharges: lineItem.hasInvoicedCharges,
  };
}

export function ProjectLineItemsForm(props: Props) {
  const { showToast } = useToast();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);
  const applyBundleDialog = useDisclosure();

  const {
    control,
    formState,
    reset,
    register,
    handleSubmit,
    setValue,
    clearErrors,
  } = useForm<FormData>({
    resolver: zodResolver(projectLineItemsFormSchema),
    reValidateMode: 'onSubmit',
    defaultValues: {
      lineItems:
        props.project.items.length === 0
          ? [
              {
                item: null,
                transactionType: TRANSACTION_TYPE.SERVICE,
                quantity: null,
                details: null,
                location: props.project.defaultLocation,
                startOn: props.project.startOn,
                endOn: props.project.endOn,
                aggregationKey: crypto.randomUUID(),
                hasInvoicedCharges: false,
              },
            ]
          : props.project.items.map((lineItem) => lineItemToFormData(lineItem)),
    },
  });

  const updateProject = useUpdateProject(props.project.key, {
    onSuccess: ({ items }) => {
      reset({
        lineItems: items.map((lineItem) => lineItemToFormData(lineItem)),
      });

      props.onSuccess();
    },
    onError: (error) => {
      if (isPlanLimitError(error)) {
        return;
      }

      // Client-side Zod validation already catches the field-level cases
      // (item required, quantity non-negative, date phases). The invoiced-charge
      // lock is a handled validation error: surface its curated message rather
      // than the support snackbar. Anything else is unexpected — report to
      // Sentry and surface the support snackbar so the team is alerted.
      const apiError = handleAPIError(error);
      if (apiError.errorType === 'project_has_invoiced_charges') {
        showToast({
          severity: 'error',
          element: <span>{getUserFacingErrorMessage(apiError)}</span>,
        });
        return;
      }
      if (!apiError.sentToSentry) {
        captureException(error);
      }
      setShowSnackbar(true);
    },
  });

  const { fields, append, insert, remove } = useFieldArray({
    control,
    name: 'lineItems',
  });

  const { isDirty } = formState;

  const cascadingRef = useRef(false);
  const errorsRef = useRef(formState.errors);
  errorsRef.current = formState.errors;
  const flourish = useLinkedFieldFlourish();

  // Expose submit/cancel to parent via onFormReady
  const submitRef = useRef<() => void>(noop);
  const cancelRef = useRef<() => void>(noop);

  const watchedLineItems = useWatch({ control, name: 'lineItems' });

  // When an end date changes, cascade the next item's start date in the
  // same aggregation group to the day after. Gaps are not allowed. If the
  // cascade would push next.start past next.end (inverting the range),
  // skip it so validation surfaces the conflict instead of silently
  // distorting the next phase's range.
  const handleEndDateChange = useCallback(
    (index: number, date: DateTime | null) => {
      if (cascadingRef.current || !date?.isValid) return;
      if (date.year < 1970 || date.year > 2099) return;
      cascadingRef.current = true;

      const currentItem = watchedLineItems[index];
      if (!currentItem) {
        cascadingRef.current = false;
        return;
      }

      for (let i = index + 1; i < watchedLineItems.length; i++) {
        if (watchedLineItems[i].aggregationKey === currentItem.aggregationKey) {
          const nextStart = date.plus({ days: 1 });
          const nextEnd = watchedLineItems[i].endOn;
          if (nextEnd?.isValid && nextStart > nextEnd) {
            break;
          }
          if (!isSameDate(nextStart, watchedLineItems[i].startOn)) {
            setValue(`lineItems.${i}.startOn`, nextStart, {
              shouldDirty: true,
            });
            flourish.trigger(`lineItems.${i}.startOn`);
            if (errorsRef.current.lineItems?.[i]?.startOn) {
              clearErrors(`lineItems.${i}.startOn`);
            }
          }
          // When the next item has no end date and the project has an end,
          // auto-fill the end date so the phase spans to the project end
          if (!nextEnd && props.project.endOn) {
            setValue(`lineItems.${i}.endOn`, props.project.endOn, {
              shouldDirty: true,
            });
            flourish.trigger(`lineItems.${i}.endOn`);
            if (errorsRef.current.lineItems?.[i]?.endOn) {
              clearErrors(`lineItems.${i}.endOn`);
            }
          }
          break;
        }
      }

      cascadingRef.current = false;
    },
    [watchedLineItems, setValue, clearErrors, flourish, props.project.endOn]
  );

  // When a start date changes, cascade the previous item's end date in the
  // same aggregation group to the day before. Gaps are not allowed. If the
  // cascade would push prev.end before prev.start (inverting the range),
  // skip it so validation surfaces the conflict instead of silently
  // distorting the previous phase's range.
  const handleStartDateChange = useCallback(
    (index: number, date: DateTime | null) => {
      if (cascadingRef.current || !date?.isValid) return;
      if (date.year < 1970 || date.year > 2099) return;
      cascadingRef.current = true;

      const currentItem = watchedLineItems[index];
      if (!currentItem) {
        cascadingRef.current = false;
        return;
      }

      for (let i = index - 1; i >= 0; i--) {
        if (watchedLineItems[i].aggregationKey === currentItem.aggregationKey) {
          const prevEnd = date.minus({ days: 1 });
          const prevStart = watchedLineItems[i].startOn;
          if (prevStart?.isValid && prevEnd < prevStart) {
            break;
          }
          if (!isSameDate(prevEnd, watchedLineItems[i].endOn)) {
            setValue(`lineItems.${i}.endOn`, prevEnd, {
              shouldDirty: true,
            });
            flourish.trigger(`lineItems.${i}.endOn`);
            if (errorsRef.current.lineItems?.[i]?.endOn) {
              clearErrors(`lineItems.${i}.endOn`);
            }
          }
          break;
        }
      }

      cascadingRef.current = false;
    },
    [watchedLineItems, setValue, clearErrors, flourish]
  );

  const handleTransactionTypeChange = useCallback(
    (index: number, value: string) => {
      const currentItem = watchedLineItems[index];
      if (!currentItem) return;

      for (let i = 0; i < watchedLineItems.length; i++) {
        if (
          i !== index &&
          watchedLineItems[i].aggregationKey === currentItem.aggregationKey
        ) {
          setValue(
            `lineItems.${i}.transactionType`,
            value as FormData['lineItems'][number]['transactionType']
          );
        }
      }
    },
    [watchedLineItems, setValue]
  );

  const handleClickAdd = () => {
    append({
      id: undefined,
      item: null,
      transactionType: TRANSACTION_TYPE.SERVICE,
      quantity: null,
      details: null,
      location: props.project.defaultLocation,
      startOn: props.project.startOn,
      endOn: props.project.endOn,
      aggregationKey: crypto.randomUUID(),
      hasInvoicedCharges: false,
    });
  };

  // Projects allow duplicate rows for the same SKU, so a bundle apply
  // always appends new rows in the bundle's member order. Each appended
  // row uses the same defaults as a manually-added line (SERVICE
  // transaction type, which the user can toggle afterward) so the lines
  // behave identically to manual additions.
  const handleApplyBundle = (lines: BundleApplyLine[], bundle: Bundle) => {
    for (const line of lines) {
      append({
        id: undefined,
        item: line.item,
        transactionType: TRANSACTION_TYPE.SERVICE,
        quantity: line.quantity,
        details: null,
        location: props.project.defaultLocation,
        startOn: props.project.startOn,
        endOn: props.project.endOn,
        aggregationKey: crypto.randomUUID(),
        hasInvoicedCharges: false,
      });
    }
    showToast({
      element: buildBundleAppliedToast({
        lineCount: lines.length,
        bundleName: bundle.name,
      }),
      severity: 'success',
    });
  };

  const handleAddContinuation = (lastIndex: number) => {
    const lastItem = watchedLineItems[lastIndex];
    if (!lastItem) return;

    const dates = computeContinuationDates({
      previousEndDate: lastItem.endOn,
      billingFrequency: null,
      projectEnd: props.project.endOn,
    });

    insert(lastIndex + 1, {
      id: undefined,
      item: lastItem.item,
      transactionType: lastItem.transactionType,
      quantity: lastItem.quantity,
      details: null,
      location: lastItem.location,
      startOn: dates.startDate,
      endOn: dates.endDate,
      aggregationKey: lastItem.aggregationKey,
      hasInvoicedCharges: false,
    });
  };

  const handleCreateLocation = (name: string) => {
    const url = getNewLocationURL(name);
    window.open(url, '_blank');
  };

  const defaultLocationId = props.project.defaultLocation?.id;

  // Pin the default location and the project's locations to a priority
  // group at the top of the location dropdown
  const locationPriorityGroup = useMemo((): CustomPriorityGroup | undefined => {
    const ids = new Set<string>();
    if (props.project.defaultLocation) {
      ids.add(props.project.defaultLocation.id);
    }
    for (const location of props.project.locations) {
      ids.add(location.id);
    }

    if (ids.size === 0) {
      return undefined;
    }

    return { label: 'Project locations', ids: Array.from(ids) };
  }, [props.project.defaultLocation, props.project.locations]);

  const sortLocations = (location: SparseLocation, other: SparseLocation) => {
    // Default location sorts first; compare by zone family so a
    // zone-expanded default site stays clustered
    if (defaultLocationId) {
      const locationIsDefault =
        (isZoneOption(location) ? location._parentId : location.id) ===
        defaultLocationId;
      const otherIsDefault =
        (isZoneOption(other) ? other._parentId : other.id) ===
        defaultLocationId;

      if (locationIsDefault !== otherIsDefault) {
        return locationIsDefault ? -1 : 1;
      }
    }

    const locationGroupName = location.customer?.name ?? 'Manufacturer';
    const otherGroupName = other.customer?.name ?? 'Manufacturer';

    if (locationGroupName === otherGroupName) {
      if (location.isArchived === other.isArchived) {
        return location.name.localeCompare(other.name);
      }

      return location.isArchived ? 1 : -1;
    }

    return locationGroupName.localeCompare(otherGroupName);
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (!isDirty) {
      reset();
      props.onCancel();

      return;
    }

    const lineItems: UpdateProjectLineItem[] = data.lineItems
      .filter(
        (lineItem) => lineItem.item !== null && lineItem.quantity !== null
      )
      .map((lineItem) => {
        // Force SERVICE for non-physical items as a safety net in case
        // the toggle group state drifted during item selection
        const transactionType = lineItem.item!.isPhysical
          ? lineItem.transactionType
          : TRANSACTION_TYPE.SERVICE;

        return {
          id: lineItem.id,
          itemId: lineItem.item!.id,
          transactionType,
          count: lineItem.quantity!,
          details: lineItem.details === '' ? null : lineItem.details,
          locationId: lineItem.location?.id ?? null,
          startOn: lineItem.startOn,
          endOn: lineItem.endOn,
          aggregationKey: lineItem.aggregationKey,
        };
      });

    updateProject.mutate({ lineItems });
  };

  const handleClickCancel = () => {
    reset();
    props.onCancel();
  };

  // Keep refs up to date each render
  submitRef.current = () => handleSubmit(handleValidSubmission)();
  cancelRef.current = handleClickCancel;

  // Expose form actions to parent on mount
  const onFormReady = props.onFormReady;
  useEffect(() => {
    onFormReady?.({
      submit: () => submitRef.current(),
      cancel: () => cancelRef.current(),
    });
  }, [onFormReady]);

  // Build grouped field indices: consecutive items with the same
  // aggregationKey are rendered inside a single Paper container.
  // Use fields (sync) as fallback when watchedLineItems hasn't caught up yet.
  const fieldGroups: { groupKey: string; indices: number[] }[] = [];
  for (let i = 0; i < fields.length; i++) {
    const groupKey =
      watchedLineItems[i]?.aggregationKey ?? fields[i]?.aggregationKey ?? '';
    const last = fieldGroups[fieldGroups.length - 1];
    if (last && last.groupKey === groupKey) {
      last.indices.push(i);
    } else {
      fieldGroups.push({ groupKey, indices: [i] });
    }
  }

  const dateOrderErrors = validateLineItemDates(fieldGroups, watchedLineItems);

  return (
    <Stack
      spacing={2}
      padding={props.padding}
      overflow="auto"
      component="form"
      onSubmit={handleSubmit(handleValidSubmission)}
    >
      {fieldGroups.map((group, groupIdx) => {
        const isMultiItemGroup = group.indices.length > 1;
        const groupColor = isMultiItemGroup
          ? getGroupColor(groupIdx)
          : undefined;
        const lastIndex = group.indices[group.indices.length - 1];

        return (
          <Paper
            key={fields[group.indices[0]].id}
            variant="outlined"
            sx={{
              p: 2,
              ...(isMultiItemGroup
                ? { borderLeft: `3px solid ${groupColor}` }
                : undefined),
            }}
          >
            {group.indices.map((index, posInGroup) => {
              // Get the previous phase's end date for date picker defaults
              const prevPhaseIdx =
                posInGroup > 0 ? group.indices[posInGroup - 1] : -1;
              const previousPhaseEndDate =
                prevPhaseIdx >= 0
                  ? (watchedLineItems[prevPhaseIdx]?.endOn ?? null)
                  : null;

              const handleClickDelete = () => {
                const deletedItem = watchedLineItems[index];
                if (deletedItem) {
                  const groupKey = deletedItem.aggregationKey;
                  for (
                    let nextIdx = index + 1;
                    nextIdx < watchedLineItems.length;
                    nextIdx++
                  ) {
                    if (
                      watchedLineItems[nextIdx]?.aggregationKey === groupKey
                    ) {
                      if (deletedItem.startOn) {
                        setValue(
                          `lineItems.${nextIdx}.startOn`,
                          deletedItem.startOn,
                          { shouldDirty: true }
                        );
                        if (errorsRef.current.lineItems?.[nextIdx]?.startOn) {
                          clearErrors(`lineItems.${nextIdx}.startOn`);
                        }
                      }
                      break;
                    }
                  }
                }
                remove(index);
              };

              const watched = watchedLineItems[index];
              const duration = formatCalendarDateRangeDuration(
                watched?.startOn,
                watched?.endOn
              );

              return (
                <Box key={fields[index].id}>
                  {/* Phase header */}
                  {isMultiItemGroup && (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      px={1}
                      sx={
                        posInGroup > 0
                          ? {
                              mt: 2,
                              pt: 1.5,
                              borderTop: '1px solid',
                              borderColor: 'divider',
                            }
                          : undefined
                      }
                      mb={1.5}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color="text.secondary"
                      >
                        Phase {posInGroup + 1}
                      </Typography>
                    </Box>
                  )}
                  <ProjectLineItemFormRow
                    control={control}
                    register={register}
                    setValue={setValue}
                    clearErrors={clearErrors}
                    lineItemErrors={formState.errors.lineItems?.[index]}
                    index={index}
                    field={fields[index]}
                    watchedLineItem={watched}
                    dateOrderErrors={dateOrderErrors}
                    projectStart={props.project.startOn}
                    projectEnd={props.project.endOn}
                    customerId={props.project.customer?.id}
                    posInGroup={posInGroup}
                    isMultiItemGroup={isMultiItemGroup}
                    previousPhaseEndDate={previousPhaseEndDate}
                    flourish={flourish}
                    durationDisplay={
                      duration !== null ? (
                        <Typography variant="caption" color="text.secondary">
                          Duration: {duration}
                        </Typography>
                      ) : null
                    }
                    onDelete={handleClickDelete}
                    onTransactionTypeChange={handleTransactionTypeChange}
                    onStartDateChange={handleStartDateChange}
                    onEndDateChange={handleEndDateChange}
                    sortLocations={sortLocations}
                    locationPriorityGroup={locationPriorityGroup}
                    onCreateLocation={handleCreateLocation}
                  />
                </Box>
              );
            })}
            <Box
              mt={1}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Button
                size="small"
                onClick={() => handleAddContinuation(lastIndex)}
              >
                Add a phase for this line item
              </Button>
              {isMultiItemGroup && selectedGroupKey !== group.groupKey && (
                <Button
                  size="small"
                  onClick={() => setSelectedGroupKey(group.groupKey)}
                >
                  View timeline
                </Button>
              )}
            </Box>
            <Collapse
              in={selectedGroupKey === group.groupKey && isMultiItemGroup}
            >
              <ProjectLineItemGantt
                items={group.indices.map((idx): GanttItem => {
                  const item = watchedLineItems[idx];
                  return {
                    id: fields[idx].id,
                    label: item?.item?.sku
                      ? `${item.item.sku} (${item.item.name})`
                      : (item?.item?.name ?? ''),
                    start: item?.startOn ?? null,
                    end: item?.endOn ?? null,
                    quantity: item?.quantity ?? null,
                    unitPrice: null,
                  };
                })}
                groupColor="#063DCB"
                onClose={() => setSelectedGroupKey(null)}
              />
            </Collapse>
          </Paper>
        );
      })}
      <Box display="flex" justifyContent="space-between">
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddRounded />}
            onClick={handleClickAdd}
          >
            Add new line item
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<LibraryAddOutlined />}
            onClick={applyBundleDialog.open}
          >
            Add bundle
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button onClick={handleClickCancel}>Cancel</Button>
          <LoadingButton
            type="submit"
            variant="contained"
            color="secondary"
            loading={updateProject.isPending}
          >
            Save
          </LoadingButton>
        </Stack>
      </Box>
      <SupportSnackbar
        isOpen={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
          updateProject.reset();
        }}
        message="An error occurred saving line items"
      />
      <ApplyBundleDialog
        isOpen={applyBundleDialog.isOpen}
        onClose={applyBundleDialog.close}
        onApply={handleApplyBundle}
      />
    </Stack>
  );
}
